import { useEffect, useState } from "react";
import { Heart, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { confetti, playCoin, playFanfare } from "@/lib/celebrate";
import { UserAvatar, UserLink } from "@/components/UserBits";

export function TreasureSpot({ pathname }) {
  const { refreshUser } = useAuth();
  const [spot, setSpot] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSpot(null);
    const t = setTimeout(() => api.get("/treasure/spot", { params: { page: pathname } }).then(({ data }) => setSpot(data)).catch(() => {}), 900);
    return () => clearTimeout(t);
  }, [pathname]);
  if (!spot) return null;
  const claim = async (e) => {
    if (busy) return;
    const r = e.currentTarget.getBoundingClientRect();
    setBusy(true);
    try {
      const { data } = await api.post(`/treasure/${spot.idx}/claim`);
      confetti({ x: r.left + r.width / 2, y: r.top + r.height / 2, count: data.all ? 120 : 50, colors: ["#f6d77a", "#e8b64c", "#f43f5e", "#fff3c4"] });
      playCoin(3); if (data.all) playFanfare();
      if (data.badge) toast.success("Schatzjäger-Abzeichen freigeschaltet! 💛 7 Tage am Stück alle Herzen gefunden.", { duration: 6000 });
      toast.success(data.all ? `Alle ${data.total} Schätze gefunden! 🏆 +${data.points} Punkte` : `Schatz gefunden! 💛 +${data.points} Punkte (${data.found}/${data.total} heute)`);
      setSpot(null); refreshUser?.();
      window.dispatchEvent(new CustomEvent("treasure-found"));
    } catch (err) { toast.error(formatErr(err)); setSpot(null); } finally { setBusy(false); }
  };
  return (
    <button onClick={claim} aria-label="Versteckter Schatz" data-testid="treasure-spot"
      style={{ left: `${spot.x}%`, top: `${Math.min(160 + spot.y * 14, (document.querySelector("main")?.offsetHeight || 900) - 90)}px` }}
      className="absolute z-30 w-9 h-9 -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center treasure-spot">
      <Heart className="w-6 h-6 text-[#e8b64c] drop-shadow" fill="url(#treasure-gold)" strokeWidth={1.5} />
      <Sparkles className="absolute -top-1 -right-1 w-3.5 h-3.5 text-amber-300 treasure-twinkle" />
      <svg width="0" height="0" className="absolute"><defs><linearGradient id="treasure-gold" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fff3c4" /><stop offset=".5" stopColor="#e8b64c" /><stop offset="1" stopColor="#b8862b" /></linearGradient></defs></svg>
    </button>
  );
}

export function TreasureCard() {
  const [t, setT] = useState(null);
  useEffect(() => {
    const load = () => api.get("/treasure/today").then(({ data }) => setT(data)).catch(() => {});
    load(); window.addEventListener("treasure-found", load);
    return () => window.removeEventListener("treasure-found", load);
  }, []);
  if (!t) return null;
  const done = t.found >= t.total;
  return (
    <div className="relative overflow-hidden rounded-3xl p-5 sm:p-6 bg-gradient-to-br from-amber-50 via-white to-rose-50 border border-amber-200 fade-up" data-testid="treasure-card">
      <span className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-amber-200/40 blur-2xl pointer-events-none" />
      <div className="relative flex items-start gap-4">
        <span className="w-12 h-12 shrink-0 rounded-2xl vip-gold-metal flex items-center justify-center treasure-spot"><Heart className="w-6 h-6" fill="currentColor" /></span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-lg font-extrabold">Schatzsuche</h3>
          <p className="text-sm text-slate-600">{done ? "Du hast heute alle Schätze gefunden – morgen werden neue versteckt! 🏆" : `Heute sind ${t.total} goldene Herzen irgendwo in der App versteckt – für jeden an einer anderen Stelle. Halte die Augen offen!`}</p>
          <div className="mt-3 flex items-center gap-2" data-testid="treasure-progress">
            {Array.from({ length: t.total }, (_, i) => <Heart key={i} className={`w-6 h-6 ${i < t.found ? "text-amber-500" : "text-slate-200"}`} fill="currentColor" />)}
            <span className="text-xs font-bold text-amber-700 ml-1" data-testid="treasure-count">{t.found}/{t.total} gefunden · {t.points_today}/{t.max_points} Punkte</span>
          </div>
          <p className="mt-2 text-xs text-slate-500" data-testid="treasure-streak">
            {t.streak >= t.streak_goal ? <><b className="text-amber-700">Schatzjäger-Abzeichen aktiv 💛</b> · Serie {t.streak} Tage – finde jeden Tag alle Herzen, damit es bleibt.</>
              : <>Serie: <b className="text-amber-700">{t.streak}/{t.streak_goal} Tage</b> – finde {t.streak_goal} Tage hintereinander alle Herzen für das Schatzjäger-Abzeichen.</>}
          </p>
        </div>
      </div>
    </div>
  );
}

const TABS = [["today", "Heute"], ["total", "Gesamt"]];

function LbRow({ r, mine }) {
  return (
    <li className={`flex items-center gap-3 px-3 py-2 rounded-2xl ${mine ? "bg-amber-50 border border-amber-200" : ""}`} data-testid={`treasure-lb-row-${r.rank}`}>
      <span className={`w-7 text-center font-extrabold ${r.rank <= 3 ? "text-amber-500" : "text-slate-400"}`}>{r.rank <= 3 ? ["🥇", "🥈", "🥉"][r.rank - 1] : r.rank}</span>
      <UserAvatar userId={r.user_id} name={r.name} picture={r.picture} size="w-9 h-9" />
      <UserLink id={r.user_id} className="flex-1 min-w-0 truncate font-semibold text-sm">{r.name}{mine && <span className="text-xs text-rose-500"> (Du)</span>}</UserLink>
      <span className="shrink-0 inline-flex items-center gap-1 font-extrabold text-amber-600 text-sm" data-testid={`treasure-lb-count-${r.rank}`}><Heart className="w-4 h-4" fill="currentColor" />{r.count}</span>
    </li>
  );
}

export function TreasureLeaderboard() {
  const { user } = useAuth();
  const [scope, setScope] = useState("today");
  const [d, setD] = useState(null);
  useEffect(() => {
    const load = () => api.get("/treasure/leaderboard", { params: { scope } }).then(({ data }) => setD(data)).catch(() => setD({ top: [] }));
    setD(null); load(); window.addEventListener("treasure-found", load);
    return () => window.removeEventListener("treasure-found", load);
  }, [scope]);
  const meInTop = d?.me && d.top.some((r) => r.user_id === d.me.user_id);
  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 border border-amber-100 space-y-4" data-testid="treasure-leaderboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-display text-lg font-extrabold flex items-center gap-2"><Heart className="w-5 h-5 text-amber-500" fill="currentColor" /> Schatzjäger-Rangliste</h3>
        <div className="flex p-1 rounded-full bg-amber-50">
          {TABS.map(([k, l]) => <button key={k} onClick={() => setScope(k)} data-testid={`treasure-lb-tab-${k}`} className={`h-8 px-4 rounded-full text-xs font-bold transition-colors ${scope === k ? "vip-gold-metal" : "text-amber-700"}`}>{l}</button>)}
        </div>
      </div>
      <p className="text-xs text-slate-500">{scope === "today" ? "Wer hat heute die meisten goldenen Herzen gefunden?" : "Alle bisher gefundenen Herzen."}</p>
      {!d ? <div className="h-24 rounded-2xl bg-amber-50/60 animate-pulse" /> : d.top.length === 0
        ? <p className="text-sm text-slate-400 py-4 text-center" data-testid="treasure-lb-empty">{scope === "today" ? "Heute hat noch niemand ein Herz gefunden – sei die/der Erste! 💛" : "Noch keine Herzen gefunden."}</p>
        : <ul className="space-y-1">{d.top.map((r) => <LbRow key={r.user_id} r={r} mine={r.user_id === user?.user_id} />)}</ul>}
      {d?.me && !meInTop && <ul className="pt-2 border-t border-amber-100" data-testid="treasure-lb-me"><LbRow r={d.me} mine /></ul>}
    </div>
  );
}

export function TreasureBadge({ streak = 0, testId = "treasure-badge" }) {
  if (streak < 7) return null;
  return (
    <span title={`Schatzjäger: ${streak} Tage hintereinander alle Herzen gefunden`} data-testid={testId}
      className="climber-badge inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold vip-gold-metal shadow-md shadow-amber-300/50">
      <Heart className="w-3 h-3" fill="currentColor" /> Schatzjäger · {streak} Tage
    </span>
  );
}

export function MyTreasureBadge({ testId }) {
  const [s, setS] = useState(0);
  useEffect(() => { api.get("/treasure/today").then(({ data }) => setS(data.streak || 0)).catch(() => {}); }, []);
  return <TreasureBadge streak={s} testId={testId} />;
}
